import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const response = await api.checkAuth();
          if (response.data.authenticated) {
            setUser(response.data.user);
          } else {
            localStorage.removeItem('auth_token');
          }
        } catch (err) {
          console.error('Auth check failed:', err);
          localStorage.removeItem('auth_token');
        }
      }
      setLoading(false);
      setInitialized(true);
    };
    
    checkAuth();
  }, []);

  const login = useCallback(async (username, password) => {
    const response = await api.adminLogin(username, password);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('auth_token', access_token);
    setUser(userData);
    
    return userData;
  }, []);

  const ssoLogin = useCallback(async (accessToken) => {
    const response = await api.ssoLogin(accessToken);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('auth_token', access_token);
    setUser(userData);
    
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    setUser(null);
  }, []);

  const isStaff = user?.is_staff || false;
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      initialized,
      isAuthenticated,
      isStaff,
      login,
      ssoLogin,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
